/**
 * `<rt-search-box>`: progressive enhancement of the server-rendered header search form
 * (port of the old `Header.tsx` search).
 *
 * Without JS the wrapped `<form action="/search"><input name="q">` submits normally. With JS:
 * - debounced suggestions from `GET {suggest-url}?q=` (a JSON `string[]`), cached per query,
 *   stale requests aborted; nothing is fetched until the input is focused (the old header
 *   fetched suggestions on every page view);
 * - ARIA combobox/listbox, ↑/↓ moves through suggestions and fills the input (as before),
 *   Escape closes, clicking outside closes;
 * - on submit, pasted YouTube URLs jump straight to the archive page (`url-routing.ts`).
 */
import { debounce } from '../lib/debounce';
import { Island, defineElement, h } from '../lib/dom';
import { array, decode, string } from '../lib/validate';
import { routeHref, routeSearchInput } from './url-routing';

export const SUGGEST_DEBOUNCE_MS = 200;
export const MAX_SUGGESTIONS = 10;
export const MAX_QUERY_LENGTH = 200;
const suggestionsDecoder = array(string({ maxLength: 500 }), { maxLength: 100 });

let instance = 0;

export class RtSearchBox extends Island {
  #input!: HTMLInputElement;
  #list!: HTMLUListElement;
  #suggestUrl = '/api/v2/search/suggest';
  #typed = '';
  #suggestions: readonly string[] = [];
  #selected = -1;
  #open = false;
  readonly #cache = new Map<string, readonly string[]>();
  #inflight: AbortController | null = null;
  readonly #id = `rt-search-${String(++instance)}`;
  /** Navigation hook (overridable in tests). */
  navigate: (href: string) => void = (href) => {
    window.location.assign(href);
  };

  protected start(signal: AbortSignal): void {
    const form = this.querySelector('form');
    const input = this.querySelector<HTMLInputElement>('input[name="q"]');
    if (!form || !input) {
      this.renderFailure('<rt-search-box> needs a <form> with an <input name="q">');
      return;
    }
    this.#input = input;
    this.#suggestUrl = this.getAttribute('suggest-url') ?? this.#suggestUrl;
    this.#typed = input.value;

    if (getComputedStyle(form).position === 'static') form.classList.add('relative');
    this.#list = h('ul', {
      class:
        // Flush under the input (same edges and background) so input + list read as one panel.
        'absolute inset-x-0 top-full z-20 overflow-hidden rounded-b border-t border-gray-700 bg-gray-800 py-1 shadow-lg shadow-black/50',
      attrs: {
        id: `${this.#id}-list`,
        role: 'listbox',
        hidden: true,
        'aria-label': 'Search suggestions',
      },
    });
    form.querySelector(':scope > ul[role="listbox"]')?.remove();
    form.append(this.#list);

    input.setAttribute('role', 'combobox');
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', this.#list.id);
    input.setAttribute('aria-expanded', 'false');

    const fetchDebounced = debounce(
      (q: string) => void this.#fetchSuggestions(q),
      SUGGEST_DEBOUNCE_MS,
    );
    signal.addEventListener('abort', () => {
      fetchDebounced.cancel();
      this.#inflight?.abort();
    });

    input.addEventListener(
      'focus',
      () => {
        this.#setOpen(true);
        fetchDebounced(input.value);
      },
      { signal },
    );
    input.addEventListener(
      'input',
      () => {
        this.#typed = input.value;
        this.#selected = -1;
        fetchDebounced(input.value);
      },
      { signal },
    );
    input.addEventListener(
      'keydown',
      (e) => {
        this.#onKeyDown(e);
      },
      { signal },
    );
    this.#list.addEventListener(
      'mousedown',
      (e) => {
        e.preventDefault(); // keep focus in the input
      },
      { signal },
    );
    this.#list.addEventListener(
      'click',
      (e) => {
        const option = (e.target as Element | null)?.closest<HTMLLIElement>('[role="option"]');
        const value = option?.dataset['value'];
        if (value === undefined) return;
        input.value = value;
        this.navigate(routeHref({ kind: 'search', q: value }));
      },
      { signal },
    );
    document.addEventListener(
      'pointerdown',
      (e) => {
        if (!this.contains(e.target as Node | null)) this.#setOpen(false);
      },
      { signal },
    );
    form.addEventListener(
      'submit',
      (e) => {
        const route = routeSearchInput(input.value);
        if (route.kind === 'search') return; // normal GET /search?q=
        e.preventDefault();
        this.navigate(routeHref(route));
      },
      { signal },
    );
  }

  protected override stop(): void {
    this.#inflight?.abort();
  }

  #onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.#setOpen(true);
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      this.#selected = Math.min(Math.max(-1, this.#selected + delta), this.#suggestions.length - 1);
      // The old header filled the input with the highlighted suggestion (or the typed text at -1).
      this.#input.value = this.#suggestions[this.#selected] ?? this.#typed;
      this.#renderList();
    } else if (e.key === 'Escape') {
      if (this.#open) {
        e.preventDefault();
        this.#input.value = this.#typed;
        this.#selected = -1;
        this.#setOpen(false);
      }
    }
  }

  async #fetchSuggestions(raw: string): Promise<void> {
    const q = raw.trim().slice(0, MAX_QUERY_LENGTH);
    const cached = this.#cache.get(q);
    if (cached) {
      this.#show(cached);
      return;
    }
    this.#inflight?.abort();
    const controller = new AbortController();
    this.#inflight = controller;
    try {
      const sep = this.#suggestUrl.includes('?') ? '&' : '?';
      const res = await fetch(`${this.#suggestUrl}${sep}q=${encodeURIComponent(q)}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const result = decode(suggestionsDecoder, await res.json());
      if (!result.ok) {
        console.warn('[rt-search-box] bad suggest response:', result.error);
        return;
      }
      const list = result.value.slice(0, MAX_SUGGESTIONS);
      this.#cache.set(q, list);
      // Ignore responses for text the user has since changed.
      if (this.#typed.trim().slice(0, MAX_QUERY_LENGTH) === q) this.#show(list);
    } catch (error) {
      if (!controller.signal.aborted) console.warn('[rt-search-box] suggest failed', error);
    } finally {
      if (this.#inflight === controller) this.#inflight = null;
    }
  }

  #show(list: readonly string[]): void {
    // Don't replace the list while the user is arrowing through it (old behaviour).
    if (this.#selected >= 0) return;
    this.#suggestions = list;
    this.#renderList();
  }

  #setOpen(open: boolean): void {
    this.#open = open;
    this.#renderList();
  }

  #renderList(): void {
    const visible = this.#open && this.#suggestions.length > 0;
    this.#list.hidden = !visible;
    this.#input.setAttribute('aria-expanded', String(visible));
    this.#list.replaceChildren(
      ...this.#suggestions.map((s, i) =>
        h(
          'li',
          {
            class: `block cursor-pointer truncate px-4 py-1.5 transition-colors duration-100 hover:bg-gray-700${i === this.#selected ? ' bg-gray-700' : ''}`,
            attrs: {
              id: `${this.#id}-opt-${String(i)}`,
              role: 'option',
              'aria-selected': String(i === this.#selected),
              'data-value': s,
              title: s,
            },
          },
          s,
        ),
      ),
    );
    if (visible && this.#selected >= 0)
      this.#input.setAttribute(
        'aria-activedescendant',
        `${this.#id}-opt-${String(this.#selected)}`,
      );
    else this.#input.removeAttribute('aria-activedescendant');
  }
}

defineElement('rt-search-box', RtSearchBox);

declare global {
  interface HTMLElementTagNameMap {
    'rt-search-box': RtSearchBox;
  }
}
